import { useState, type ReactNode } from 'react';
import { Button, Input } from '@paige/ui';

type Save = (name: string) => Promise<void>;

/** Sending a name: busy while it goes, and why it failed. */
function useSave(onSave: Save, onClose: () => void) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const save = async (text: string) => {
    setBusy(true);
    setFailed(null);
    try {
      await onSave(text.trim());
      onClose();
    } catch (error) {
      setFailed(error instanceof Error ? error.message : 'Could not save the name.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, failed, save };
}

/** The open field: the name, Save and Cancel, and why a save failed. */
function RenameForm({
  name,
  onSave,
  onClose,
}: {
  name: string;
  onSave: Save;
  onClose: () => void;
}): ReactNode {
  const [text, setText] = useState(name);
  const { busy, failed, save } = useSave(onSave, onClose);
  return (
    <form className="dg-rename-form" onSubmit={(e) => (e.preventDefault(), void save(text))}>
      <Input
        aria-label="Machine name"
        maxLength={60}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <Button type="submit" size="sm" disabled={busy}>
        Save
      </Button>
      <Button type="button" size="sm" variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      {failed ? <span className="dg-error">{failed}</span> : null}
    </form>
  );
}

/**
 * Renames a machine in place: a small "Rename" that opens a field with its current name; Save sends it, an empty name
 * clears it (the machine's own name shows). Staff only; absent when the screen is given no way to save.
 */
export function RenameMachine({ name, onSave }: { name: string; onSave: Save }): ReactNode {
  const [open, setOpen] = useState(false);
  return open ? (
    <RenameForm name={name} onSave={onSave} onClose={() => setOpen(false)} />
  ) : (
    <button type="button" className="dg-rename" onClick={() => setOpen(true)}>
      Rename
    </button>
  );
}
